import { type RequiredFieldsForClassification } from "@/src/lib/schema-types";
import { isFieldRequired as baseIsFieldRequired } from "@/src/lib/utils/object-utils";
import { BlockForKey } from "@/src/models/intake/blocks/block";
import { BlockConfigFile, BlockRule, FieldRule } from "@/src/models/intake/blocks/block-config";
import { BlockKey, isBlockKey } from "@/src/models/intake/blocks/block-types";
import { type EnrichedContent, type EnrichedContentBlocks } from "@/src/models/intake/blocks/enriched-content";
import { Section } from "@/src/models/section";

export function isRosterOnlyBlock(blockKey: BlockKey | undefined): boolean {
  switch (blockKey) {
    case BlockKey.RECEIVING_PROVIDER:
    case BlockKey.RECEIVING_LOCATION:
      return true;
    default:
      return false;
  }
}

export function shouldClearReviewedFieldsForInsufficientExtraction(
  blockKey: BlockKey,
  isRequired: boolean,
  passesFieldThreshold: boolean
): boolean {
  return !passesFieldThreshold && (!isRequired || isRosterOnlyBlock(blockKey));
}

export function getBlockRuleForKey(
  blockConfigFile: BlockConfigFile | undefined | null,
  blockKey: BlockKey
): BlockRule | null {
  if (blockConfigFile == null) return null;

  return Object.values(blockConfigFile.blocks).find((rule) => rule.type === blockKey) ?? null;
}

/** Updates matching rules in an already resolved block configuration. */
export function updateBlockRuleForKey(
  blockConfig: BlockConfigFile,
  blockKey: BlockKey,
  updates: Partial<Omit<BlockRule, "type" | "extends">>
): BlockConfigFile {
  return {
    ...blockConfig,
    blocks: Object.fromEntries(
      Object.entries(blockConfig.blocks).map(([key, rule]) => [
        key,
        rule.type === blockKey ? { ...rule, ...updates } : rule,
      ])
    ),
  };
}

/**
 * Get the field rule for a field path from block config.
 * Returns the field rule if found, or null if not configured.
 * Note: The caller should check fieldRule.enabled !== false before using it.
 */
export function getFieldRule(blockRule: BlockRule | null, fieldPath: string): FieldRule | null {
  if (blockRule == null || blockRule.fields == null) return null;

  return blockRule.fields[fieldPath] ?? null;
}

/**
 * Returns configured fields that do not have a more specific configured descendant.
 * Parent rules configure compound fields, while terminal rules drive extraction and entity updates.
 */
export function getTerminalFieldEntries(fields: BlockRule["fields"]): Array<[string, FieldRule]> {
  const entries = Object.entries(fields ?? {});

  return entries.filter(([path]) => !entries.some(([candidate]) => candidate.startsWith(`${path}.`)));
}

export function getFieldOptions(
  blockConfig: BlockConfigFile | undefined | null,
  blockKey: BlockKey,
  fieldPath: string
): readonly string[] | undefined {
  const blockRule = getBlockRuleForKey(blockConfig, blockKey);
  const fieldRule = getFieldRule(blockRule, fieldPath);

  return fieldRule?.options;
}

export function getFieldOptionLabels(
  blockConfig: BlockConfigFile | undefined | null,
  blockKey: BlockKey,
  fieldPath: string
): Readonly<Record<string, string>> | undefined {
  const blockRule = getBlockRuleForKey(blockConfig, blockKey);
  const fieldRule = getFieldRule(blockRule, fieldPath);

  return fieldRule?.optionLabels;
}

export function isBlockEnabled(blockConfig: BlockConfigFile | undefined | null, blockKey: BlockKey): boolean {
  if (blockConfig == null) return true;

  const blockRule = getBlockRuleForKey(blockConfig, blockKey);

  return blockRule?.enabled !== false;
}

/**
 * Returns block keys that are enabled in block config.
 */
export function getEnabledBlockKeys(
  blockKeys: readonly BlockKey[],
  blockConfig: BlockConfigFile | undefined
): BlockKey[] {
  return blockKeys.filter((k) => isBlockEnabled(blockConfig, k));
}

export function isFieldEnabled(
  blockConfig: BlockConfigFile | undefined | null,
  blockKey: BlockKey,
  fieldPath: string
): boolean {
  if (blockConfig == null) {
    return true;
  }

  const blockRule = getBlockRuleForKey(blockConfig, blockKey);
  const fieldRule = getFieldRule(blockRule, fieldPath);

  // Field is enabled by default if not configured; only hide when explicitly disabled
  return fieldRule?.enabled !== false;
}

export function isFieldRequiredFromBlockConfig(
  blockConfig: BlockConfigFile | undefined | null,
  blockKey: BlockKey,
  fieldPath: string,
  valuesObject: Record<string, unknown>
): boolean {
  const blockRule = getBlockRuleForKey(blockConfig, blockKey);
  const requirements = blockRule?.requirements;

  if (requirements == null) return false;

  const requiredConfig: RequiredFieldsForClassification = {
    requiredFields: requirements.requiredFields ?? [],
    defaults: {},
    conditionallyRequiredFields: requirements.conditionallyRequiredFields ?? {},
    alternativeRequiredFields: requirements.alternativeRequiredFields,
  };

  return baseIsFieldRequired(requiredConfig, valuesObject, fieldPath);
}

/**
 * Check if a block has any requirements configured.
 * A block is considered to have requirements if it has:
 * - requiredFields (non-empty array), OR
 * - conditionallyRequiredFields with at least one non-self dependency, OR
 * - alternativeRequiredFields (non-empty array)
 *
 * Note: Conditional rules like dependsOn: "$." (normalized to blockKey)
 * mean "required only if values are present for this block" and do not
 * make the whole block required.
 */
export function isBlockRequired(blockRule: BlockRule | undefined | null): boolean {
  const requirements = blockRule?.requirements;

  if (requirements == null) return false;

  const hasRequiredFields = (requirements.requiredFields?.length ?? 0) > 0;
  const selfDependency = blockRule?.type;
  const hasConditionalRequirements = Object.values(requirements.conditionallyRequiredFields ?? {}).some(
    (rule) => rule.dependsOn !== selfDependency && rule.dependsOn !== "$."
  );
  const hasAlternativeRequirements = (requirements.alternativeRequiredFields?.length ?? 0) > 0;

  return hasRequiredFields || hasConditionalRequirements || hasAlternativeRequirements;
}

/**
 * Check if a field should be visible in the main UI (not in the hidden section).
 * A field is visible if:
 * - It is enabled, AND
 * - It is required, primary, or secondary
 *
 * Tertiary fields that are not required should go in the "Hidden" section.
 */
export function isFieldVisible(
  blockConfig: BlockConfigFile | undefined | null,
  blockKey: BlockKey,
  fieldPath: string,
  valuesObject: Record<string, unknown>
): boolean {
  // First check if field is enabled
  if (!isFieldEnabled(blockConfig, blockKey, fieldPath)) {
    return false;
  }

  // Get field rule to check importance
  const blockRule = getBlockRuleForKey(blockConfig, blockKey);
  const fieldRule = getFieldRule(blockRule, fieldPath);

  // Default importance is "tertiary" if not specified
  const importance = fieldRule?.importance ?? "tertiary";

  // If field is tertiary, only show in main UI if it's required
  if (importance === "tertiary") {
    return isFieldRequiredFromBlockConfig(blockConfig, blockKey, fieldPath, valuesObject);
  }

  // Primary and secondary fields are always visible (if enabled)
  return true;
}

/**
 * Check if a field should be in the "Hidden" collapsible section.
 * A field is hidden if it is enabled but not visible (i.e., tertiary and not required).
 */
export function isFieldHidden(
  blockConfig: BlockConfigFile | undefined | null,
  blockKey: BlockKey,
  fieldPath: string,
  valuesObject: Record<string, unknown>
): boolean {
  return (
    isFieldEnabled(blockConfig, blockKey, fieldPath) && !isFieldVisible(blockConfig, blockKey, fieldPath, valuesObject)
  );
}

/**
 * Returns full field paths for all enabled fields in the block config.
 * Used internally by getFieldPathsToUpdateFromBlockConfig.
 */
function getEnabledFieldPathsFromBlockConfig(
  blockConfig: BlockConfigFile | undefined | null,
  blockKey: BlockKey
): string[] {
  const blockRule = getBlockRuleForKey(blockConfig, blockKey);

  if (blockRule == null || blockRule.fields == null) return [];

  return getTerminalFieldEntries(blockRule.fields)
    .filter(([, rule]) => rule.enabled !== false)
    .map(([path]) => path);
}

/**
 * Returns all full field paths to update when the user selects an entity (patient, provider, location, coverage).
 * Combines system fields (id, displayId, etc.) with enabled config fields so each block only needs one call.
 */
export function getFieldPathsToUpdateFromBlockConfig(
  blockConfig: BlockConfigFile | undefined | null,
  blockKey: BlockKey
): string[] {
  let systemLeafKeys: string[];
  let nestedSystemPaths: string[] = [];

  switch (blockKey) {
    case BlockKey.PATIENT:
      systemLeafKeys = ["displayId", "id"];
      break;
    case BlockKey.PRIMARY_CARE_PROVIDER:
    case BlockKey.ORDERING_PROVIDER:
    case BlockKey.REFERRING_PROVIDER:
    case BlockKey.RECEIVING_PROVIDER:
    case BlockKey.PROVIDER:
      systemLeafKeys = ["displayId", "id", "npiNumber", "npiRegistryEntry", "role"];
      break;
    case BlockKey.RECEIVING_LOCATION:
    case BlockKey.REFERRING_LOCATION:
      systemLeafKeys = ["displayId", "id", "placeId", "role"];
      nestedSystemPaths = [`${blockKey}.address.id`];
      break;
    case BlockKey.PRIMARY_COVERAGE:
    case BlockKey.SECONDARY_COVERAGE:
      systemLeafKeys = ["id"];
      break;
    default:
      systemLeafKeys = [];
  }

  const systemPaths = systemLeafKeys.map((leaf) => `${blockKey}.${leaf}`);
  const enabledPaths = getEnabledFieldPathsFromBlockConfig(blockConfig, blockKey);

  return [...systemPaths, ...nestedSystemPaths, ...enabledPaths];
}

// Sources the intake read-only stages can project into a block's reviewedFields for display,
// since read-only mode has no reviewed content of its own yet.
export type ReadOnlySourceField = "documentExtractedFields" | "initialReviewedFields";

// Read-only intake stages (extraction/enrichment) render the same block components review uses,
// but before a human has reviewed anything — this swaps in the stage-appropriate snapshot field
// as reviewedFields so those components can render unmodified.
export function projectEnrichedContentBySourceField(
  content: EnrichedContentBlocks,
  sourceField: ReadOnlySourceField
): EnrichedContentBlocks {
  const projected = { ...content };

  function projectBlock<K extends BlockKey>(key: K) {
    const block = content[key];

    if (block) projected[key] = { ...block, reviewedFields: block[sourceField] ?? null };
  }

  Object.keys(content).filter(isBlockKey).forEach(projectBlock);

  return projected;
}

export function getReviewedFieldsForKey<K extends BlockKey>(
  content: EnrichedContent | null,
  key: K
): BlockForKey<K>["reviewedFields"] | null {
  return content?.[key]?.reviewedFields ?? null;
}

export function getReceivingProviderNpi(section: Section): string | null {
  if (!section.enrichedContent) return null;

  const enrichedContent = section.enrichedContent;
  const block = enrichedContent[BlockKey.RECEIVING_PROVIDER];

  return block?.reviewedFields?.npiNumber ?? null;
}
